/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk } from '@reduxjs/toolkit'
import { IFaxCoversheet, IFaxDocument, IFaxMessage } from '@dataTypes/fax.type'
import {
  bulkDeleteFaxById,
  bulkUpdateFaxById,
  createFax,
  deleteFaxById,
  getAllFax,
  getFaxCounts,
  updateFaxById,
} from '@services/api/fax/fax.api'
import { getFaxSettings, IFaxSettings } from '@services/api/fax/settings.api'
import { RootState } from '@store/index'

export const fetchFaxSettings = createAsyncThunk<
  IFaxSettings | null,
  void,
  { rejectValue: string }
>('api/fax/fetch/settings', async (_, { rejectWithValue }) => {
  try {
    const response = await getFaxSettings()
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }
    return response.data?.fax_settings ?? null
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

export const fetchFaxCounts = createAsyncThunk<
  Record<string, number>,
  void,
  { rejectValue: string }
>('api/fax/fetch/counts', async (_, { rejectWithValue }) => {
  try {
    const response = await getFaxCounts()
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }
    if (response.data && 'counts' in response.data && response.data.counts) {
      return response.data.counts
    }
    return rejectWithValue('Fax counts not found in the response')
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

export const fetchAllFax = createAsyncThunk<
  IFaxMessage[],
  string | undefined,
  { rejectValue: string }
>('api/fax/fetch/all', async (view, { rejectWithValue, dispatch }) => {
  try {
    const response = await getAllFax(undefined, view)
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }

    if (
      response.data &&
      'fax' in response.data &&
      Array.isArray(response.data.fax)
    ) {
      dispatch(fetchFaxCounts())
      return response.data.fax as IFaxMessage[]
    }

    return rejectWithValue('Fax messages not found in the response')
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

export const addFax = createAsyncThunk<
  IFaxMessage | void,
  { fax: IFaxMessage; coversheet?: IFaxCoversheet; documents?: IFaxDocument[] },
  {
    rejectValue: string
  }
>('api/fax/add', async (data, { dispatch, rejectWithValue }) => {
  try {
    const response = await createFax({
      ...data.fax,
      intent: data.fax.intent,
      to_number: data.fax.to_number?.length ? data.fax.to_number : null,
      from_number: data.fax.from_number?.length ? data.fax.from_number : null,
      fax_coversheet_id: data.fax.fax_coversheet_id ?? undefined,
      cover_snapshot_html: data.fax.cover_snapshot_html ?? undefined,
      cover_snapshot_text: data.fax.cover_snapshot_text ?? undefined,
      coversheet_mode: data.fax.coversheet_mode ?? undefined,
      draft_compose_state: data.fax.draft_compose_state ?? undefined,
      documents: data.documents?.map((doc, index) => ({
        file_url: doc.file_url,
        file_name: doc.file_name,
        mime_type: doc.mime_type,
        file_size: doc.file_size,
        sort_order: doc.sort_order ?? index,
      })),
    })

    if (response.statusCode !== 201) {
      return rejectWithValue(response.message || 'Failed to add fax')
    }

    if ('fax' in response.data && response.data.fax != null) {
      const fax = response.data.fax
      const createdFax =
        typeof fax === 'object' && !Array.isArray(fax)
          ? (fax as IFaxMessage)
          : undefined
      await dispatch(fetchAllFax())
      return createdFax
    }
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to create fax')
  }
})

export const updateFax = createAsyncThunk<
  IFaxMessage,
  { fax_message_id: string; data: Partial<IFaxMessage> },
  { rejectValue: string }
>(
  'api/fax/update',
  async ({ fax_message_id, data }, { getState, rejectWithValue }) => {
    try {
      const selectedClinic = (getState() as RootState).clinic.selectedClinic
      const clinicId = selectedClinic?.clinic_id as string
      const response = await updateFaxById(fax_message_id, {
        ...data,
        clinic_id: clinicId,
      })

      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }

      if (
        response.data &&
        'fax' in response.data &&
        typeof response.data.fax === 'object' &&
        !Array.isArray(response.data.fax)
      ) {
        return response.data.fax as IFaxMessage
      }

      return rejectWithValue('Fax message not found in the response')
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error occurred during update')
    }
  },
)

export const bulkUpdateFax = createAsyncThunk<
  IFaxMessage[],
  { ids: string[]; data: Partial<IFaxMessage> },
  { rejectValue: string }
>('api/fax/update/bulk', async ({ ids, data }, { rejectWithValue }) => {
  try {
    const response = await bulkUpdateFaxById(ids, data)

    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }

    if (
      response.data &&
      'fax' in response.data &&
      Array.isArray(response.data.fax)
    ) {
      return response.data.fax as IFaxMessage[]
    }

    return rejectWithValue('Fax messages not found in the response')
  } catch (error: any) {
    return rejectWithValue(error.message || 'Error occurred during bulk update')
  }
})

export const bulkDeleteFax = createAsyncThunk<
  string[],
  string[],
  { rejectValue: string }
>('api/fax/bulkDelete', async (ids, { rejectWithValue }) => {
  try {
    const response = await bulkDeleteFaxById(ids)
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }
    return ids
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

export const deleteFax = createAsyncThunk<
  string,
  string,
  {
    rejectValue: string
  }
>('api/fax/delete', async (id, { rejectWithValue }) => {
  try {
    const response = await deleteFaxById(id)
    if (response.statusCode !== 200) {
      throw new Error(response.message)
    }
    return id
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})
